import { api } from './client';

export interface HelpdeskTagDto {
  id: string;
  slug: string;
  name: string;
  color: string;
}

export interface HelpdeskTicketDto {
  id: string;
  primarySpaceId: string;
  accountId: string;
  ticketCode: string | null;
  accountName: string;
  accountEmail: string;
  accountLanguage: string;
  visibility: string;
  subject: string;
  fromEmail: string;
  fromName: string | null;
  fromNameVocative: string | null;
  contactMatchStatus: 'pending' | 'matched' | 'not_found' | null;
  contactMatchType: 'partner_contact' | 'lead' | 'affiliate_contact' | null;
  contactMatchId: string | null;
  contactCardUrl: string | null;
  contactFullName: string | null;
  contactCompany: string | null;
  contactRole: string | null;
  contactMatchedAt: string | null;
  status: string;
  urgent: boolean;
  assigneeType: string | null;
  assigneeUserId: string | null;
  assigneeUserName: string | null;
  assigneeAiAgentId: string | null;
  linkedRondoUserId: string | null;
  aiHandled: boolean;
  hasHumanReply: boolean;
  importedFrom: string;
  lastMessageAt: string | null;
  lastMessageDirection: string | null;
  createdAt: string;
  updatedAt: string;
  tags: HelpdeskTagDto[];
  links: { spaceId: string; relationType: string }[];
}

export interface HelpdeskAttachmentDto {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
}

export interface HelpdeskMessageDto {
  id: string;
  messageType: string;
  authorType: string;
  authorUserId: string | null;
  authorUserName: string | null;
  authorAiAgentId: string | null;
  linkedSpaceId: string | null;
  subject: string | null;
  bodyPlain: string | null;
  bodyHtml: string | null;
  sentAt: string;
  attachments: HelpdeskAttachmentDto[];
}

export interface HelpdeskActivityDto {
  id: string;
  actorType: string;
  userId: string | null;
  userName: string | null;
  action: string;
  payload: unknown;
  createdAt: string;
}

export interface HelpdeskTicketDetailDto extends HelpdeskTicketDto {
  canAddToCard: boolean;
  messages: HelpdeskMessageDto[];
  activity: HelpdeskActivityDto[];
}

export interface HelpdeskListResult {
  tickets: HelpdeskTicketDto[];
}

export interface HelpdeskListParams {
  spaceId?: string;
  status?: string;
  assigneeUserId?: string;
  /** 'me' | 'unassigned' (mine / no assignee) - parity with legacy filter. */
  assignee?: string;
  urgent?: boolean;
  aiOnly?: boolean;
  search?: string;
}

export interface HelpdeskStatsFilters { period?: string; month?: string; spaceId?: string; agent?: string }
export interface HelpdeskStatsOverview {
  total: number;
  byStatus: Record<string, number>;
  resolved: number;
  open: number;
  spam: number;
  resolvedPct: number;
  avgFirstReplyMinutes: number | null;
  avgResolveMinutes: number | null;
  perAccount: { accountId: number; name: string; total: number; resolved: number; open: number; resolvedPct: number }[];
  perLanguage: { language: string; count: number }[];
  timeline: { day: string; count: number }[];
  perOperator?: {
    userId: number; name: string; assigned: number; resolved: number; open: number;
    replies: number; notes: number; avgFirstReplyMinutes: number | null; resolvedPct: number;
  }[];
}
export interface HelpdeskTopics {
  topics: { tagId: number; slug: string; name: string; color: string; total: number; resolved: number; avgResolveMinutes: number | null; manualCount: number; aiCount: number }[];
  untagged: number;
  taggedTotal: number;
}

export interface HelpdeskAssignees {
  users: { id: string; name: string }[];
  aiAgents: { id: string; name: string; slug: string }[];
}

export type HelpdeskAssignee =
  | null
  | { type: 'user'; userId: string }
  | { type: 'ai'; aiAgentId: string };

const base = '/api/v1/helpdesk';

export const helpdeskApi = {
  list: (params?: HelpdeskListParams) => {
    const q = new URLSearchParams();
    if (params?.spaceId) q.set('spaceId', params.spaceId);
    if (params?.status) q.set('status', params.status);
    if (params?.assigneeUserId) q.set('assigneeUserId', params.assigneeUserId);
    if (params?.assignee) q.set('assignee', params.assignee);
    if (params?.urgent) q.set('urgent', '1');
    if (params?.aiOnly) q.set('aiOnly', '1');
    if (params?.search) q.set('search', params.search);
    const qs = q.toString();
    return api.get<HelpdeskListResult>(`${base}/tickets${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => api.get<HelpdeskTicketDetailDto>(`${base}/tickets/${id}`),

  assignees: () => api.get<HelpdeskAssignees>(`${base}/assignees`),
  tags: () => api.get<HelpdeskTagDto[]>(`${base}/tags`),

  stats: (f: HelpdeskStatsFilters) => {
    const q = new URLSearchParams();
    if (f.period) q.set('period', f.period);
    if (f.month) q.set('month', f.month);
    if (f.spaceId) q.set('spaceId', f.spaceId);
    if (f.agent) q.set('agent', f.agent);
    const qs = q.toString();
    return api.get<HelpdeskStatsOverview>(`${base}/stats${qs ? `?${qs}` : ''}`);
  },
  statsTopics: (f: HelpdeskStatsFilters) => {
    const q = new URLSearchParams();
    if (f.period) q.set('period', f.period);
    if (f.month) q.set('month', f.month);
    if (f.spaceId) q.set('spaceId', f.spaceId);
    if (f.agent) q.set('agent', f.agent);
    const qs = q.toString();
    return api.get<HelpdeskTopics>(`${base}/stats/topics${qs ? `?${qs}` : ''}`);
  },
  statsAiInsights: () => api.get<{ status: string; reason?: string }>(`${base}/stats/ai-insights`),
  cardTargets: (id: string, q: string) =>
    api.get<HelpdeskCardTarget[]>(`${base}/tickets/${id}/card-targets?q=${encodeURIComponent(q)}`),
  suggestVocative: (id: string, name: string) =>
    api.get<{ vocative: string }>(`${base}/tickets/${id}/vocative?name=${encodeURIComponent(name)}`),
  addToCard: (id: string, input: { kind: HelpdeskCardKind; targetId: string; name: string; vocative: string; position: string }) =>
    api.post<{ matched: boolean }>(`${base}/tickets/${id}/contact-card`, input),
  ticketsByEmail: (email: string) => api.get<HelpdeskListResult>(`${base}/tickets-by-email?email=${encodeURIComponent(email)}`),

  setStatus: (id: string, status: string) => api.patch<{ id: string }>(`${base}/tickets/${id}`, { status }),
  setUrgent: (id: string, urgent: boolean) => api.patch<{ id: string }>(`${base}/tickets/${id}`, { urgent }),
  setAssignee: (id: string, assignee: HelpdeskAssignee) => api.patch<{ id: string }>(`${base}/tickets/${id}`, { assignee }),
  note: (id: string, body: string) => api.post<{ id: string }>(`${base}/tickets/${id}/note`, { body }),
  reply: (id: string, body: string, setStatus?: string) =>
    api.post<{ id: string; sent: false }>(`${base}/tickets/${id}/reply`, { body, setStatus }),
  archive: (id: string) => api.delete<{ id: string }>(`${base}/tickets/${id}`),
  addTag: (id: string, tagId: string) => api.post<{ id: string }>(`${base}/tickets/${id}/tags`, { tagId: Number(tagId) }),
  removeTag: (id: string, tagId: string) => api.delete<{ id: string }>(`${base}/tickets/${id}/tags/${tagId}`),
  attachmentDownloadUrl: (ticketId: string, attachmentId: string) =>
    `${base}/tickets/${ticketId}/attachments/${attachmentId}/download`,
  linkRondoUser: (id: string, linkedRondoUserId: string | null) =>
    api.patch<{ id: string }>(`${base}/tickets/${id}/link-user`, { linkedRondoUserId }),
  deleteMessage: (id: string, messageId: string) => api.delete<{ id: string }>(`${base}/tickets/${id}/messages/${messageId}`),
};

// Rondo user search for the ticket link picker (reuses the Rondo module's
// read-only endpoint; Rondo end-users are test data per the data catalogue).
export type HelpdeskCardKind = 'klient' | 'affiliate';
export interface HelpdeskCardTarget { kind: HelpdeskCardKind; id: string; name: string; detail: string }

export interface RondoUserHit { id: number; name: string; nickname: string | null; email: string | null }
export function searchRondoUsers(search: string) {
  const q = new URLSearchParams({ search, limit: '8' });
  return api.get<{ items: RondoUserHit[]; total: number }>(`/api/v1/rondo/users?${q.toString()}`);
}
